import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';

const DEFAULT_PREFS = {
  essential: true,
  analytics: false,
  marketing: false,
  version: '1.0',
  timestamp: null,
};

export default function CookieConsentBanner({ policyUrl = '/policy/cookies' }) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showManage, setShowManage] = useState(false);

  // Add CSS animations
  useEffect(() => {
    const styleId = 'cookie-banner-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(5deg);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    const loadCookiePreferences = async () => {
      try {
        // Utilisateur toujours connecté dans le Dashboard : chargement direct via API
        const response = await apiClient.get('/api/user/preferences/cookies');
        
        if (response.data && response.data.success) {
          const serverPrefs = response.data.cookies;
          
          // Convertir le format serveur vers le format local
          const localPrefs = {
            essential: serverPrefs.essential,
            analytics: serverPrefs.analytics,
            marketing: serverPrefs.marketing,
            timestamp: serverPrefs.timestamp,
            version: serverPrefs.version || DEFAULT_PREFS.version
          };
          
          setPrefs(localPrefs);
          
          // Broadcast pour les autres modules
          window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: localPrefs }));
          window.getCookieConsent = () => localPrefs;
          
          // Si l'utilisateur n'a jamais donné de consentement, afficher la bannière
          if (!serverPrefs.timestamp) {
            setShowBanner(true);
          }
        } else {
          // Utilisateur sans préférences : afficher la bannière
          setShowBanner(true);
        }
      } catch (error) {
        console.error('❌ Failed to load cookie preferences:', error);
        // En cas d'erreur API = afficher bannière par sécurité
        setShowBanner(true);
      }
    };

    loadCookiePreferences();
  }, []);

  const saveAndBroadcast = async (newPrefs) => {
    const withMeta = { ...newPrefs, timestamp: new Date().toISOString(), version: DEFAULT_PREFS.version };
    
    try {
      // Utilisateur toujours connecté dans le Dashboard : sauvegarde directe via API
      const response = await apiClient.put('/api/user/preferences/cookies', {
        essential: withMeta.essential,
        analytics: withMeta.analytics,
        marketing: withMeta.marketing,
        version: withMeta.version
      });
      
      if (response.data && response.data.success) {
        console.log('✅ Cookie preferences saved successfully');
        
        // Mettre à jour l'état local avec les données du serveur
        const serverPrefs = response.data.cookies;
        const localPrefs = {
          essential: serverPrefs.essential,
          analytics: serverPrefs.analytics,
          marketing: serverPrefs.marketing,
          timestamp: serverPrefs.timestamp,
          version: serverPrefs.version
        };
        
        setPrefs(localPrefs);
        
        // Broadcast events pour les autres modules
        window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: localPrefs }));
        window.getCookieConsent = () => localPrefs;
        
      } else {
        throw new Error('API response indicated failure');
      }
      
    } catch (error) {
      console.error('❌ Error saving cookie preferences:', error);
      
      // En cas d'erreur, au moins garder l'état local temporairement
      setPrefs(withMeta);
      window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: withMeta }));
      window.getCookieConsent = () => withMeta;
      
      // TODO: Afficher une notification d'erreur à l'utilisateur
    }
  };

  const acceptAll = () => {
    saveAndBroadcast({ ...DEFAULT_PREFS, analytics: true, marketing: true });
    setShowBanner(false);
    setShowManage(false);
  };

  const rejectNonEssential = () => {
    saveAndBroadcast({ ...DEFAULT_PREFS, analytics: false, marketing: false });
    setShowBanner(false);
    setShowManage(false);
  };

  const saveManage = (overrides) => {
    saveAndBroadcast({ ...DEFAULT_PREFS, ...overrides });
    setShowBanner(false);
    setShowManage(false);
  };

  if (!showBanner && !showManage) return null;

  return (
    <>
      {showBanner && (
        <div style={{ 
          position: 'fixed', 
          left: 16, 
          right: 16, 
          bottom: 16, 
          zIndex: 6000,
          animation: 'slideUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            gap: 16, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            borderRadius: 16, 
            padding: 20, 
            boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3), 0 8px 16px rgba(0,0,0,0.15)', 
            border: '1px solid rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 60,
              height: 60,
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              animation: 'float 3s ease-in-out infinite'
            }} />
            <div style={{
              position: 'absolute',
              bottom: -10,
              left: -10,
              width: 40,
              height: 40,
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '50%',
              animation: 'float 3s ease-in-out infinite 1s'
            }} />
            
            <div style={{ flex: 1, color: 'white' }}>
              <div style={{ 
                fontWeight: 700, 
                marginBottom: 8, 
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <span style={{ fontSize: 20 }}>🍪</span>
                {t('cookieConsent.title', 'Nous utilisons des cookies')}
              </div>
              <div style={{ 
                fontSize: 14, 
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.4
              }}>
                {t('cookieConsent.desc', "Nous utilisons des cookies pour améliorer votre expérience. ")}
                <a 
                  href={policyUrl} 
                  target="_blank" 
                  rel="noreferrer" 
                  style={{ 
                    color: 'white',
                    textDecoration: 'underline',
                    textDecorationColor: 'rgba(255,255,255,0.6)',
                    marginLeft: 6,
                    fontWeight: 500,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecorationColor = 'white'}
                  onMouseLeave={(e) => e.target.style.textDecorationColor = 'rgba(255,255,255,0.6)'}
                >
                  {t('cookieConsent.learnMore', 'En savoir plus')}
                </a>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button 
                onClick={() => setShowManage(true)} 
                style={{ 
                  padding: '10px 16px', 
                  background: 'rgba(255,255,255,0.15)', 
                  borderRadius: 10, 
                  border: '1px solid rgba(255,255,255,0.2)', 
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: 500,
                  fontSize: 14,
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(5px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.25)';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.15)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                ⚙️ {t('cookieConsent.manage', 'Gérer')}
              </button>
              
              <button 
                onClick={rejectNonEssential} 
                style={{ 
                  padding: '10px 16px', 
                  background: 'rgba(255,255,255,0.9)', 
                  borderRadius: 10, 
                  border: 'none', 
                  cursor: 'pointer',
                  color: '#4B5563',
                  fontWeight: 500,
                  fontSize: 14,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.9)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                ❌ {t('cookieConsent.reject', 'Refuser')}
              </button>
              
              <button 
                onClick={acceptAll} 
                style={{ 
                  padding: '10px 20px', 
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', 
                  color: 'white', 
                  borderRadius: 10, 
                  border: 'none', 
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                ✨ {t('cookieConsent.accept', 'Accepter')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showManage && (
        <div style={{ 
          position: 'fixed', 
          inset: 0, 
          zIndex: 7000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: 16,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{ 
            width: 'min(500px, 90%)', 
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
            borderRadius: 20, 
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)', 
            padding: 24,
            position: 'relative',
            border: '1px solid rgba(255,255,255,0.2)',
            animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}>
            {/* Decorative header gradient */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
              borderRadius: '20px 20px 0 0'
            }} />
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12, 
              marginBottom: 12 
            }}>
              <span style={{ fontSize: 24 }}>🍪</span>
              <h3 style={{ 
                margin: 0, 
                fontSize: 20, 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {t('cookieConsent.managePrefsTitle', 'Préférences de cookies')}
              </h3>
            </div>
            
            <p style={{ 
              marginTop: 0, 
              color: '#64748B', 
              fontSize: 14,
              lineHeight: 1.5,
              marginBottom: 20
            }}>
              {t('cookieConsent.managePrefsDesc', 'Personnalisez vos préférences de cookies pour une expérience optimale.')}
            </p>

            <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: 16,
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: 12,
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <div>
                  <div style={{ 
                    fontWeight: 600, 
                    color: '#065F46',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    🔒 Essentiel
                  </div>
                  <div style={{ fontSize: 13, color: '#047857', marginTop: 4 }}>
                    Nécessaire au fonctionnement du site
                  </div>
                </div>
                <div style={{ 
                  color: '#059669', 
                  fontSize: 12,
                  fontWeight: 500,
                  padding: '4px 8px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: 6
                }}>
                  Toujours actif
                </div>
              </div>

              <PreferenceRow 
                icon="📊"
                label={t('cookieConsent.analytics', 'Analytics')} 
                desc={t('cookieConsent.analyticsDesc', 'Statistiques anonymes pour améliorer le produit')} 
                defaultValue={prefs?.analytics || false} 
                onChange={(v) => setPrefs(prev => ({ ...(prev || DEFAULT_PREFS), analytics: v }))} 
              />

              <PreferenceRow 
                icon="🎯"
                label={t('cookieConsent.marketing', 'Marketing')} 
                desc={t('cookieConsent.marketingDesc', 'Personnalisation et publicités')} 
                defaultValue={prefs?.marketing || false} 
                onChange={(v) => setPrefs(prev => ({ ...(prev || DEFAULT_PREFS), marketing: v }))} 
              />
            </div>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 12, 
              marginTop: 24 
            }}>
              <button 
                onClick={() => { setShowManage(false); setShowBanner(true); }} 
                style={{ 
                  padding: '10px 20px', 
                  background: 'rgba(100, 116, 139, 0.1)', 
                  borderRadius: 10, 
                  border: '1px solid rgba(100, 116, 139, 0.2)',
                  cursor: 'pointer',
                  color: '#475569',
                  fontWeight: 500,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(100, 116, 139, 0.15)';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(100, 116, 139, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {t('actions.cancel', 'Annuler')}
              </button>
              
              <button 
                onClick={() => saveManage({ ...(prefs || DEFAULT_PREFS) })} 
                style={{ 
                  padding: '10px 24px', 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                  color: 'white', 
                  borderRadius: 10, 
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                }}
              >
                💾 {t('actions.save', 'Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PreferenceRow({ icon, label, desc, defaultValue, onChange }) {
  const [val, setVal] = useState(!!defaultValue);
  useEffect(() => setVal(!!defaultValue), [defaultValue]);
  
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      padding: 16,
      background: val ? 'rgba(102, 126, 234, 0.05)' : 'rgba(100, 116, 139, 0.05)',
      borderRadius: 12,
      border: `1px solid ${val ? 'rgba(102, 126, 234, 0.15)' : 'rgba(100, 116, 139, 0.1)'}`,
      transition: 'all 0.3s ease'
    }}>
      <div>
        <div style={{ 
          fontWeight: 600,
          color: val ? '#4338CA' : '#475569',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 4
        }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          {label}
        </div>
        <div style={{ 
          fontSize: 13, 
          color: val ? '#6366F1' : '#64748B',
          lineHeight: 1.4
        }}>
          {desc}
        </div>
      </div>
      
      <label style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        cursor: 'pointer' 
      }}>
        <input 
          type="checkbox" 
          checked={val} 
          onChange={(e) => { 
            setVal(e.target.checked); 
            onChange && onChange(e.target.checked); 
          }} 
          style={{ display: 'none' }} 
        />
        <div style={{ 
          width: 48, 
          height: 26, 
          background: val ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#CBD5E1', 
          borderRadius: 999, 
          padding: 2, 
          display: 'flex', 
          alignItems: 'center', 
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: val ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          <div style={{ 
            width: 22, 
            height: 22, 
            background: 'white', 
            borderRadius: 999, 
            transform: val ? 'translateX(22px)' : 'translateX(0)', 
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10
          }}>
            {val ? '✨' : ''}
          </div>
        </div>
      </label>
    </div>
  );
}
